export interface DeliveryEventServiceArea {
  code: string;
  description: string;
}

export interface DeliveryEventCoordinates {
  latitude: number | null;
  longitude: number | null;
}

export interface DeliveryEventAddress {
  city: string;
  state: string;
  street: string;
  country: string;
  postalCode: string;
  coordinates: DeliveryEventCoordinates;
}

export interface DeliveryEventProvider {
  key: string;
  name: string;
  alias: string;
  country: string;
  homepage: string;
}

export interface DeliveryEventTimeRaw {
  date: string;
  time: string;
  timezone: string;
}

/**
 * The API has returned two delivery-event shapes over time. Keep the legacy
 * fields so saved orders remain renderable while exposing the richer carrier
 * payload used by current order_items.delivery_events values.
 */
export interface DeliveryEvent {
  date: string;
  time: string;
  typeCode: string;
  description: string;
  serviceArea: DeliveryEventServiceArea[];
  stage?: string;
  address?: DeliveryEventAddress;
  location?: string;
  provider?: DeliveryEventProvider;
  timeIso?: string;
  timeUtc?: string;
  timeRaw?: DeliveryEventTimeRaw;
  subStatus?: string;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown, maxLength = 240) {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  return String(value).trim().slice(0, maxLength);
}

function nullableNumber(value: unknown) {
  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function parseJsonString(value: string): unknown {
  let startIndex = 0;
  while (startIndex < value.length && value.charCodeAt(startIndex) <= 31) {
    startIndex += 1;
  }

  const candidate = value.slice(startIndex).trim();
  if (!candidate) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(candidate) as unknown;
    return typeof parsedValue === 'string' ? parseJsonString(parsedValue) : parsedValue;
  } catch {
    return null;
  }
}

function normalizeServiceArea(value: unknown): DeliveryEventServiceArea | null {
  if (!isRecord(value)) {
    return null;
  }

  const code = stringValue(value.code, 24);
  const description = stringValue(value.description, 120);
  return code || description ? { code, description } : null;
}

function normalizeAddress(value: unknown): DeliveryEventAddress | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const coordinates = isRecord(value.coordinates) ? value.coordinates : {};
  const address: DeliveryEventAddress = {
    city: stringValue(value.city, 120),
    state: stringValue(value.state, 80),
    street: stringValue(value.street, 160),
    country: stringValue(value.country, 40),
    postalCode: stringValue(value.postal_code ?? value.postalCode, 40),
    coordinates: {
      latitude: nullableNumber(coordinates.latitude),
      longitude: nullableNumber(coordinates.longitude),
    },
  };

  return Object.values(address).some((entry) => (
    typeof entry === 'string' ? Boolean(entry) : entry.latitude !== null || entry.longitude !== null
  ))
    ? address
    : undefined;
}

function normalizeProvider(value: unknown): DeliveryEventProvider | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const provider: DeliveryEventProvider = {
    key: stringValue(value.key, 40),
    name: stringValue(value.name, 80),
    alias: stringValue(value.alias, 80),
    country: stringValue(value.country, 40),
    homepage: stringValue(value.homepage, 240),
  };

  return Object.values(provider).some(Boolean) ? provider : undefined;
}

function normalizeTimeRaw(value: unknown): DeliveryEventTimeRaw | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const timeRaw: DeliveryEventTimeRaw = {
    date: stringValue(value.date, 40),
    time: stringValue(value.time, 40),
    timezone: stringValue(value.timezone, 80),
  };

  return Object.values(timeRaw).some(Boolean) ? timeRaw : undefined;
}

function normalizeEvent(value: unknown): DeliveryEvent | null {
  if (!isRecord(value)) {
    return null;
  }

  const rawLegacyServiceArea = value.serviceArea ?? value.service_area;
  const legacyServiceArea = (Array.isArray(rawLegacyServiceArea) ? rawLegacyServiceArea : [])
    .map((entry) => normalizeServiceArea(entry))
    .filter((entry): entry is DeliveryEventServiceArea => entry !== null);
  const stage = stringValue(value.stage, 40);
  const location = stringValue(value.location, 160);
  const timeIso = stringValue(value.time_iso ?? value.timeIso, 48);
  const timeUtc = stringValue(value.time_utc ?? value.timeUtc, 48);
  const address = normalizeAddress(value.address);
  const provider = normalizeProvider(value.provider);
  const timeRaw = normalizeTimeRaw(value.time_raw ?? value.timeRaw);
  const serviceArea = legacyServiceArea.length > 0
    ? legacyServiceArea
    : location
      ? [{ code: '', description: location }]
      : [];
  const date = stringValue(value.date, 32) || timeIso;
  const time = stringValue(value.time, 32);
  const typeCode = stringValue(value.typeCode ?? value.type_code, 24) || stage;
  const description = stringValue(value.description, 240);

  if (!date && !time && !typeCode && !description && !location && !address && !provider) {
    return null;
  }

  const normalizedEvent: DeliveryEvent = {
    date,
    time,
    typeCode,
    description,
    serviceArea,
  };

  if (stage) normalizedEvent.stage = stage;
  if (address) normalizedEvent.address = address;
  if (location) normalizedEvent.location = location;
  if (provider) normalizedEvent.provider = provider;
  if (timeIso) normalizedEvent.timeIso = timeIso;
  if (timeUtc) normalizedEvent.timeUtc = timeUtc;
  if (timeRaw) normalizedEvent.timeRaw = timeRaw;

  const subStatus = stringValue(value.sub_status ?? value.subStatus, 80);
  if (subStatus) normalizedEvent.subStatus = subStatus;

  return normalizedEvent;
}

function collectEventValues(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectEventValues(entry));
  }

  if (typeof value === 'string') {
    const parsedValue = parseJsonString(value);
    return parsedValue === null ? [] : collectEventValues(parsedValue);
  }

  return [value];
}

export function normalizeDeliveryEvents(value: unknown): DeliveryEvent[] {
  return collectEventValues(value)
    .map((entry) => normalizeEvent(entry))
    .filter((entry): entry is DeliveryEvent => entry !== null);
}

export function getDeliveryEventTimestamp(event: DeliveryEvent) {
  const candidates = [event.timeIso, event.timeUtc, event.date && `${event.date}T${event.time}`];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const timestamp = Date.parse(candidate);
    if (Number.isFinite(timestamp)) return timestamp;
  }

  return Number.NEGATIVE_INFINITY;
}

export function getLatestDeliveryEvent<T extends DeliveryEvent>(events: T[]) {
  return events.reduce<T | undefined>((latest, event) => {
    if (!latest || getDeliveryEventTimestamp(event) > getDeliveryEventTimestamp(latest)) {
      return event;
    }

    return latest;
  }, undefined);
}

export interface DeliveryEventMetadataRow {
  label: string;
  value: string;
}

export function getDeliveryEventMetadata(event: DeliveryEvent): DeliveryEventMetadataRow[] {
  const rows: DeliveryEventMetadataRow[] = [];
  const addRow = (label: string, value: unknown) => {
    const normalizedValue = stringValue(value, 240);
    if (normalizedValue) rows.push({ label, value: normalizedValue });
  };

  addRow('Stage', event.stage);
  addRow('Location', event.location);
  addRow('Time (local)', event.timeIso);
  addRow('Sub-status', event.subStatus);

  if (event.timeRaw) {
    addRow('Raw date', event.timeRaw.date);
    addRow('Raw time', event.timeRaw.time);
    addRow('Raw timezone', event.timeRaw.timezone);
  }

  if (event.provider) {
    addRow('Provider', event.provider.name || event.provider.alias);
    addRow('Provider alias', event.provider.alias);
    addRow('Provider country', event.provider.country);
  }

  if (event.address) {
    addRow('City', event.address.city);
    addRow('State', event.address.state);
    addRow('Street', event.address.street);
    addRow('Postal code', event.address.postalCode);
    addRow('Country', event.address.country);
  }

  return rows;
}
